import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi } from '../../api/adminApi';
import type { EvalDataset, DatasetCategory } from '../../types';
import { DATASET_CATEGORIES } from '../../types';
import { format } from 'date-fns';

export default function EvalDatasets() {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterCategory, setFilterCategory] = useState<DatasetCategory | ''>('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['evalDatasets', filterCategory],
    queryFn: () => adminApi.getEvalDatasets(filterCategory || undefined),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteEvalDataset(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evalDatasets'] });
    },
  });

  const handleDelete = (dataset: EvalDataset) => {
    if (confirm(`Delete dataset "${dataset.name}"?\n\nThis will also delete ${dataset.metadata.inputCount} inputs and ${dataset.metadata.labeledCount} labels.`)) {
      deleteMutation.mutate(dataset.id);
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading datasets...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>Error loading datasets: {error instanceof Error ? error.message : 'Unknown error'}</p>
      </div>
    );
  }

  const datasets = data?.datasets || [];

  return (
    <div className="eval-datasets-page">
      <div className="page-header">
        <h1>Evaluation Datasets</h1>
        <div className="header-actions">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value as DatasetCategory | '')}
            className="category-filter"
          >
            <option value="">All Categories</option>
            {Object.entries(DATASET_CATEGORIES).map(([key]) => (
              <option key={key} value={key}>{key.replace('_', ' ')}</option>
            ))}
          </select>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            + Create Dataset
          </button>
        </div>
      </div>

      {datasets.length === 0 ? (
        <div className="empty-state">
          <h3>No Datasets</h3>
          <p>Create your first evaluation dataset to get started.</p>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            Create Dataset
          </button>
        </div>
      ) : (
        <div className="datasets-grid">
          {datasets.map((dataset) => (
            <DatasetCard
              key={dataset.id}
              dataset={dataset}
              onDelete={() => handleDelete(dataset)}
            />
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateDatasetModal onClose={() => setShowCreateModal(false)} />
      )}

      <style>{`
        .eval-datasets-page {
          padding: 24px;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .page-header h1 {
          margin: 0;
          font-size: 24px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .header-actions {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .category-filter {
          padding: 8px 12px;
          border: 1px solid var(--border-color);
          border-radius: 6px;
          font-size: 14px;
          background: var(--bg-primary);
          color: var(--text-primary);
        }

        .btn {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          border: none;
          transition: all 0.2s;
        }

        .btn-primary {
          background: var(--color-accent);
          color: white;
        }

        .btn-primary:hover {
          background: var(--color-primary-hover);
        }

        .btn-secondary {
          background: var(--bg-tertiary);
          color: var(--text-primary);
          border: 1px solid var(--border-color-dark);
        }

        .btn-secondary:hover {
          background: var(--border-color);
        }

        .btn-danger {
          background: var(--color-error);
          color: white;
        }

        .btn-danger:hover {
          background: #b91c1c;
        }

        .datasets-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 20px;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          background: var(--bg-secondary);
          border-radius: 12px;
        }

        .empty-state h3 {
          margin: 0 0 8px;
          color: var(--text-primary);
        }

        .empty-state p {
          margin: 0 0 20px;
          color: var(--text-secondary);
        }

        .loading-container, .error-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px;
          color: var(--text-secondary);
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--border-color);
          border-top-color: var(--color-accent);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function DatasetCard({ dataset, onDelete }: { dataset: EvalDataset; onDelete: () => void }) {
  const labeledPercent = dataset.metadata.inputCount > 0
    ? Math.round((dataset.metadata.labeledCount / dataset.metadata.inputCount) * 100)
    : 0;

  const categoryColors: Record<DatasetCategory, string> = {
    golden: '#f59e0b',
    development: '#3b82f6',
    edge_cases: '#ef4444',
    subject_specific: '#10b981',
  };

  return (
    <div className="dataset-card">
      <div className="card-header">
        <span
          className="category-badge"
          style={{ backgroundColor: categoryColors[dataset.category] }}
        >
          {dataset.category.replace('_', ' ')}
        </span>
        <span className="version">v{dataset.metadata.version}</span>
      </div>

      <h3 className="dataset-name">{dataset.name}</h3>
      <p className="dataset-id">{dataset.readableId}</p>
      <p className="dataset-description">{dataset.description || 'No description'}</p>

      <div className="stat-cards stat-cards--auto">
        <div className="stat-card stat-card--fact">
          <span className="stat-card-value">{dataset.metadata.inputCount}</span>
          <span className="stat-card-label">Inputs</span>
        </div>
        <div className="stat-card stat-card--fact">
          <span className="stat-card-value">{dataset.metadata.labeledCount}</span>
          <span className="stat-card-label">Labeled</span>
        </div>
        <div className="stat-card stat-card--fact">
          <span className="stat-card-value">{labeledPercent}%</span>
          <span className="stat-card-label">Progress</span>
        </div>
      </div>

      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${labeledPercent}%` }} />
      </div>

      <div className="card-footer">
        <span className="updated-at">
          Updated {format(new Date(dataset.metadata.updatedAt), 'MMM d, yyyy')}
        </span>
        <div className="card-actions">
          <Link to={`/eval/datasets/${dataset.id}`} className="btn btn-secondary btn-sm">
            View
          </Link>
          <button className="btn btn-danger btn-sm" onClick={onDelete}>
            Delete
          </button>
        </div>
      </div>

      <style>{`
        .dataset-card {
          background: var(--bg-primary);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          padding: 20px;
          transition: box-shadow 0.2s;
        }

        .dataset-card:hover {
          box-shadow: var(--shadow-md);
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .category-badge {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          color: white;
          text-transform: capitalize;
        }

        .version {
          font-size: 12px;
          color: var(--text-secondary);
        }

        .dataset-name {
          margin: 0 0 4px;
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .dataset-id {
          margin: 0 0 8px;
          font-size: 13px;
          color: var(--text-secondary);
          font-family: monospace;
        }

        .dataset-description {
          margin: 0 0 16px;
          font-size: 14px;
          color: var(--text-secondary);
          line-height: 1.5;
        }

        /* Stat cards use the shared .stat-card--fact scheme (stat-cards.css). */

        .progress-bar {
          height: 6px;
          background: var(--border-color);
          border-radius: 3px;
          margin-bottom: 16px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: var(--color-success);
          border-radius: 3px;
          transition: width 0.3s;
        }

        .card-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .updated-at {
          font-size: 12px;
          color: var(--text-tertiary);
        }

        .card-actions {
          display: flex;
          gap: 8px;
        }

        .btn-sm {
          padding: 6px 12px;
          font-size: 12px;
        }
      `}</style>
    </div>
  );
}

function CreateDatasetModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    readableId: '',
    name: '',
    description: '',
    category: 'development' as DatasetCategory,
  });

  const createMutation = useMutation({
    mutationFn: () => adminApi.createEvalDataset(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evalDatasets'] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate();
  };

  // Auto-generate readableId from name
  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      readableId: prev.readableId || name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
    }));
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <h2>Create Dataset</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g., JEE Physics Golden Set"
              required
            />
          </div>

          <div className="form-group">
            <label>Readable ID *</label>
            <input
              type="text"
              value={formData.readableId}
              onChange={(e) => setFormData(prev => ({ ...prev, readableId: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
              placeholder="e.g., jee-physics-golden"
              pattern="[a-z0-9-]+"
              required
            />
            <span className="hint">Lowercase letters, numbers, and hyphens only</span>
          </div>

          <div className="form-group">
            <label>Category *</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as DatasetCategory }))}
              required
            >
              {Object.entries(DATASET_CATEGORIES).map(([key, desc]) => (
                <option key={key} value={key}>{key.replace('_', ' ')} - {desc}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Describe the purpose of this dataset..."
              rows={3}
            />
          </div>

          {createMutation.error && (
            <div className="error-message">
              {createMutation.error instanceof Error ? createMutation.error.message : 'Failed to create dataset'}
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating...' : 'Create Dataset'}
            </button>
          </div>
        </form>

        <style>{`
          .modal-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
          }

          .modal-content {
            background: var(--bg-primary);
            border-radius: 12px;
            padding: 24px;
            width: 100%;
            max-width: 500px;
            max-height: 90vh;
            overflow-y: auto;
          }

          .modal-content h2 {
            margin: 0 0 20px;
            font-size: 20px;
            font-weight: 600;
            color: var(--text-primary);
          }

          .form-group {
            margin-bottom: 16px;
          }

          .form-group label {
            display: block;
            margin-bottom: 6px;
            font-size: 14px;
            font-weight: 500;
            color: var(--text-primary);
          }

          .form-group input,
          .form-group select,
          .form-group textarea {
            width: 100%;
            padding: 10px 12px;
            border: 1px solid var(--border-color-dark);
            border-radius: 6px;
            font-size: 14px;
            background: var(--bg-primary);
            color: var(--text-primary);
          }

          .form-group input:focus,
          .form-group select:focus,
          .form-group textarea:focus {
            outline: none;
            border-color: var(--color-accent);
            box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
          }

          .form-group .hint {
            display: block;
            margin-top: 4px;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .error-message {
            padding: 12px;
            background: var(--color-error-bg);
            border: 1px solid var(--status-error-border);
            border-radius: 6px;
            color: var(--color-error);
            font-size: 14px;
            margin-bottom: 16px;
          }

          .modal-actions {
            display: flex;
            justify-content: flex-end;
            gap: 12px;
            margin-top: 20px;
          }
        `}</style>
      </div>
    </div>
  );
}
